import { safeGet } from '../../core/utils/safe-get.js';
import { isCategoricalAxis } from '../types/guards.js';
import {
  AREA_VARIANT,
  BAR_VARIANT,
  type DatapointMetadata,
  type BarSeriesWithDatapoints,
  type AreaSeriesWithDatapoints,
  type BarMetadata,
  type AreaMetadata,
  type BaseMetadata,
} from '../types/xy-chart-internals.js';
import {
  BAR_VARIANT_SINGLE,
  BAR_VARIANT_STACK,
  type AreaDatapoint,
  type BarDatapoint,
  type XYChartBarSeriesActionItem,
  type XYChartAreaSeriesActionItem,
} from '../types/xy-chart.js';

type SeriesMap = {
  bar: {
    Series: BarSeriesWithDatapoints;
    Metadata: BarMetadata;
    Datapoint: BarDatapoint;
  };
  area: {
    Series: AreaSeriesWithDatapoints;
    Metadata: AreaMetadata;
    Datapoint: AreaDatapoint;
  };
};

type SeriesKey = keyof SeriesMap;
type SeriesWithDatapoints<K extends SeriesKey> = SeriesMap[K]['Series'] & {
  type: K;
};
type SeriesMetadata<K extends keyof SeriesMap> = SeriesMap[K]['Metadata'];
type SeriesDatapoint<K extends keyof SeriesMap> = SeriesMap[K]['Datapoint'];

type SeriesFilterStrategy<T extends keyof SeriesMap> = (
  series: SeriesWithDatapoints<T>,
  currentMetadata: SeriesMetadata<T>,
  seriesToMetadata: Map<SeriesWithDatapoints<T>, SeriesMetadata<T>>,
  haveSameGrowingAxis: boolean,
) => boolean;

interface BuildSeriesActionItemParams<T extends keyof SeriesMap> {
  datapoint: SeriesDatapoint<T>;
  allSeries: SeriesWithDatapoints<T>[];
  seriesToMetadata: Map<SeriesWithDatapoints<T>, SeriesMetadata<T>>;
  datapointMetadataMap: Map<SeriesDatapoint<T>, DatapointMetadata>;
  currentSeries: SeriesWithDatapoints<T>;
}

function baselineDirectionToAccessor(
  baseline: 'x' | 'y' | undefined,
): 'x0' | 'y0' | undefined {
  return baseline ? `${baseline}0` : undefined;
}

function getBaselineCategoryInfo<T extends keyof SeriesMap>(
  datapoint: SeriesDatapoint<T>,
  baselineAccessor: 'x0' | 'y0',
  metadata: BaseMetadata,
): { category: string | undefined; isBaselineCategorical: boolean } {
  const { xAxis, yAxis } = metadata;
  const axis = baselineAccessor === 'x0' ? xAxis : yAxis;
  const value = datapoint[baselineAccessor];

  if (!isCategoricalAxis(axis) || typeof value !== 'string') {
    return {
      category: undefined,
      isBaselineCategorical: false,
    };
  }

  return {
    category: value,
    isBaselineCategorical: true,
  };
}

const barSeriesFilterStrategy: SeriesFilterStrategy<typeof BAR_VARIANT> = (
  _series,
  currentMetadata,
  _seriesToMetadata,
  haveSameGrowingAxis,
) => {
  return currentMetadata.variant === BAR_VARIANT_STACK && !haveSameGrowingAxis;
};

const areaSeriesFilterStrategy: SeriesFilterStrategy<typeof AREA_VARIANT> = (
  series,
  _currentMetadata,
  seriesToMetadata,
  haveSameGrowingAxis,
) => {
  if (!haveSameGrowingAxis) {
    return true;
  }

  const seriesMetadata = safeGet(seriesToMetadata, series);
  return seriesMetadata.variant === 'band';
};

const areaSimpleActionItemStrategy = (metadata: AreaMetadata) =>
  metadata.variant === 'band';

const barSimpleActionItemStrategy = (metadata: BarMetadata) =>
  metadata.variant === BAR_VARIANT_SINGLE;

interface BuildSeriesActionItemParamsInternal<T extends keyof SeriesMap> {
  datapoint: SeriesDatapoint<T>;
  allSeries: SeriesWithDatapoints<T>[];
  seriesToMetadata: Map<SeriesWithDatapoints<T>, SeriesMetadata<T>>;
  datapointMetadataMap: Map<SeriesDatapoint<T>, DatapointMetadata>;
  currentSeries: SeriesWithDatapoints<T>;
  simpleActionItemStrategy: (metadata: SeriesMetadata<T>) => boolean;
  seriesFilterStrategy: SeriesFilterStrategy<T>;
}

/**
 * Internal generic function to build action items for series with categorical baseline axes.
 * Supports both bar and area series by accepting strategy functions for variant-specific logic.
 */
function buildSeriesActionItemInternal<T extends keyof SeriesMap>({
  datapoint,
  allSeries,
  seriesToMetadata,
  datapointMetadataMap,
  currentSeries,
  simpleActionItemStrategy,
  seriesFilterStrategy,
}: BuildSeriesActionItemParamsInternal<T>) {
  const currentMetadata = safeGet(seriesToMetadata, currentSeries);
  const { baseline } = safeGet(datapointMetadataMap, datapoint);
  const baselineAccessor = baselineDirectionToAccessor(baseline);

  if (!baselineAccessor || simpleActionItemStrategy(currentMetadata)) {
    return { datapoint, name: currentMetadata.name };
  }

  const { category, isBaselineCategorical } = getBaselineCategoryInfo(
    datapoint,
    baselineAccessor,
    currentMetadata,
  );

  if (!isBaselineCategorical || category === undefined) {
    return { datapoint, name: currentMetadata.name };
  }

  const relatedDatapoints: SeriesDatapoint<T>[] = [];
  const primaryAxisAccessor = baseline === 'x' ? 'xAxisId' : 'yAxisId';
  const growingAxisAccessor = baseline === 'x' ? 'yAxisId' : 'xAxisId';

  for (const series of allSeries) {
    const haveSamePrimaryAxis =
      series[primaryAxisAccessor] === currentSeries[primaryAxisAccessor];
    const haveSameGrowingAxis =
      series[growingAxisAccessor] === currentSeries[growingAxisAccessor];

    if (
      !haveSamePrimaryAxis ||
      seriesFilterStrategy(
        series,
        currentMetadata,
        seriesToMetadata,
        haveSameGrowingAxis,
      )
    ) {
      continue;
    }

    for (const dp of series.data) {
      if (dp[baselineAccessor] === category) {
        relatedDatapoints.push(dp);
      }
    }
  }

  return {
    datapoint,
    category,
    datapoints: relatedDatapoints,
    name: currentMetadata.name,
  };
}

export function buildBarSeriesActionItem(
  params: BuildSeriesActionItemParams<typeof BAR_VARIANT>,
): XYChartBarSeriesActionItem {
  return buildSeriesActionItemInternal({
    ...params,
    simpleActionItemStrategy: barSimpleActionItemStrategy,
    seriesFilterStrategy: barSeriesFilterStrategy,
  });
}

export function buildAreaSeriesActionItem(
  params: BuildSeriesActionItemParams<typeof AREA_VARIANT>,
): XYChartAreaSeriesActionItem {
  return buildSeriesActionItemInternal({
    ...params,
    simpleActionItemStrategy: areaSimpleActionItemStrategy,
    seriesFilterStrategy: areaSeriesFilterStrategy,
  });
}
